import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Phone, Mail, MapPin, Heart, ArrowRight } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_companions-hub-2/artifacts/xm3vjt0f_TCF_logo.jpg";

const committees = [
  { id: "mosque-dawah", name: "Mosque and Da'wah" },
  { id: "project-maintenance", name: "Project and Maintenance" },
  { id: "welfare-publicity", name: "Welfare and Publicity" },
  { id: "finance-investment", name: "Finance and Investment" },
  { id: "security-ethic", name: "Security and Ethics" },
];

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-900 to-primary-900 text-white">
      {/* Main Footer */}
      <div className="container-custom py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-4 mb-6">
              <img 
                src={LOGO_URL} 
                alt="TCF Logo" 
                className="h-14 w-14 rounded-full bg-white p-1 shadow-lg" 
              />
              <div>
                <h3 className="font-heading font-bold text-xl">The Companions Foundation</h3>
                <p className="text-primary-300 text-sm font-medium">Service to Humanity</p>
              </div>
            </div>
            <p className="text-gray-400 leading-relaxed mb-6">
              Dedicated to serving the Muslim community through Islamic education, welfare programs, and community development initiatives.
            </p>
            <div className="flex items-center gap-3">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center hover:bg-primary-600 transition-all hover:scale-110"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center hover:bg-pink-600 transition-all hover:scale-110"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href="https://youtube.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center hover:bg-red-600 transition-all hover:scale-110"
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a 
                href="https://wa.me/2347030399040" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center hover:bg-green-600 transition-all hover:scale-110"
                data-testid="social-whatsapp"
              >
                <Phone className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-primary-500 rounded"></span>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {[
                { to: "/", label: "Home" },
                { to: "/about", label: "About Us" },
                { to: "/media-gallery", label: "Media Gallery" },
                { to: "/events", label: "Events" },
                { to: "/contact", label: "Contact" },
              ].map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Committees */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-primary-500 rounded"></span>
              Our Committees
            </h4>
            <ul className="space-y-3">
              {committees.map((c) => (
                <li key={c.id}>
                  <Link 
                    to={`/committee/${c.id}`} 
                    className="text-gray-400 hover:text-white transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-lg mb-6 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-primary-500 rounded"></span>
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-primary-400" />
                </div>
                <span className="text-gray-400 text-sm leading-relaxed">
                  Zaid Bn Thaabit Mosque Complex,<br />
                  Unguwar Dosa, Guava Road,<br />
                  Kaduna, Nigeria
                </span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-primary-400" />
                </div>
                <div className="flex flex-col">
                  <a href="tel:+2347030399040" className="text-gray-400 hover:text-white transition-colors text-sm">
                    +234 703 039 9040
                  </a>
                  <a href="tel:+2348033662560" className="text-gray-400 hover:text-white transition-colors text-sm">
                    +234 803 366 2560
                  </a>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-primary-400" />
                </div>
                <a href="mailto:info@tcf.org" className="text-gray-400 hover:text-white transition-colors text-sm">
                  info@tcf.org
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm flex items-center gap-1">
              Made with <Heart className="w-4 h-4 text-red-500" /> for the community
            </p>
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} The Companions Foundation. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
